import React from 'react'
import { graphql } from 'gatsby'
import PageWrap from '../components/Layouts/PageWrap'
import Hero from '../components/Headers/Hero'
import Offset from '../components/Layouts/Offset'
import SectionContainer from '../components/Layouts/SectionContainer'
import MediaText from '../components/MediaText'
import WYSIWYG from '../components/WYSIWYG'
import CardInfoGroup from '../components/Cards/CardInfoGroup'
import pddImage01 from '../static/img/icon-pdd-01.svg'
import pddImage02 from '../static/img/icon-pdd-02.svg'
import pddImage03 from '../static/img/icon-pdd-03.svg'
import pddImage04 from '../static/img/icon-pdd-04.svg'
import pddImage05 from '../static/img/icon-pdd-05.svg'
import pddImage06 from '../static/img/icon-pdd-06.svg'
import pddImage07 from '../static/img/icon-pdd-07.svg'
import pddImage08 from '../static/img/icon-pdd-08.svg'
import toolkitImage from '../static/img/toolkit-phones.png'
import Image from '../components/Images/Image'
import gameboardImage from '../static/img/purpose-driven-design-process-and-tools.jpg'
import SectionHeader from '../components/Headers/SectionHeader'

const gameboardData = {
  src: gameboardImage,
  alt: 'Purpose-Driven Design Gameboard',
}

const principlesData = {
  isOnDarkBg: true,
  sectionHeader: {
    heading: 'The Principles',
  },
  cards: [
    {
      heading: 'Real value is discovered',
      subheading:
        'Understanding our client’s business, customers, goals, and  aspirations is how we bring truly innovative solutions to life.',
      image: {
        src: pddImage01,
        alt: 'Map Icon',
      },
    },
    {
      heading: 'Usefulness Depends on Empathy',
      subheading:
        'Design decisions must be rooted in solving real problems suffered by true users. Empathy comes from walking in thier shoes.',
      image: {
        src: pddImage02,
        alt: 'Bridge Icon',
      },
    },
    {
      heading: 'Collaboration Requires Learning',
      subheading:
        'We’re all in it together – and we all matter. A unified team comes from supporting each other and learning each others needs.',
      image: {
        src: pddImage03,
        alt: 'Balancy Icon',
      },
    },
    {
      heading: 'The Road to Obvious is Rigor',
      subheading:
        'Simple solutions only appear obvious after you’ve done the hard work to get there.',
      image: {
        src: pddImage04,
        alt: 'Maze Icon',
      },
    },
    {
      heading: 'You can’t guess your way to credibility',
      subheading:
        'We believe an unverified fact is just an opinion. Credible designs are based on facts and data, not opinions and personal beliefs.',
      image: {
        src: pddImage05,
        alt: 'Arrows Icon',
      },
    },
    {
      heading: 'Design for context, not device',
      subheading:
        'We consider every part of the user’s experience when we make design decisions. Where you are matters just as much as the size of your screen.',
      image: {
        src: pddImage06,
        alt: 'Corner Icon',
      },
    },
    {
      heading: 'Innovation = Aspiration ÷ Feasibility',
      subheading:
        'We ship because we design things that can be built. We focus on innovative differentiators rather than reinventing the wheel.',
      image: {
        src: pddImage07,
        alt: 'Cube Icon',
      },
    },
    {
      heading: 'Keep it Human',
      subheading:
        'Emotion is at the core of our design. True user connection is baked into our products, not just sprinkled on at the end.',
      image: {
        src: pddImage08,
        alt: 'Robot Icon',
      },
    },
  ],
}

const toolkitData = {
  isOnDarkBg: true,
  image: {
    src: toolkitImage,
    alt: 'Purpose Driven Design site on mobile',
  },
  text: {
    sectionHeader: {
      heading: 'The Toolkit',
      subheading:
        'Our toolkit is a constantly evolving knowledge and resource center, filled with the tools and exercises we use to find success.',
    },
    copy:
      '<p>Throughout our process we use many different tools, exercises, and workflows that help provide clarity through each step and bring the abstract into the concrete. Some of these ideas come from great creative minds outside of ETR but have adapted to align with our process. <a href="http://toolkit.expandtheroom.com" target="_blank" class="text-blue-100 hover:text-blue-50">Explore the Toolkit.</a></p>',
  },
}

const PurposeDrivenDesign = args => {
  return (
    <PageWrap seo={args.data?.page?.seo} noFlex noPadding>
      <div className="bg-gray-700 h-full">
        <SectionContainer className="pt-40">
          <Hero
            heading={`Purpose-Driven Design is our <span class="text-fuchsia">unique approach</span> to
          <span class="text-chartreuse">solving problems</span>.`}
            isOnDarkBg
            size300
          />
        </SectionContainer>
        <SectionContainer as="div">
          <Offset direction="left" inline>
            <div
              className="bg-white flex justify-end py-24 pr-5 md:pr-32 lg:pr-40"
              style={{ maxWidth: 1120 }}
            >
              <Offset direction="right" rounded={false}>
                <WYSIWYG
                  content={`
              <p>
                Equal parts proven process and design philosophy, Purpose-Driven
                Design is ETR’s proprietary framework for solving design
                challenges.
              </p>
              <p>
                We look for purpose in every decision we make and every pixel we
                put on a page. From strategy to implementation, we make sure
                every decision is rooted in making the user’s experience better.
                We make products that not only solve your business problems but
                also ones that users love to use. A user is anyone who interacts
                with a product: from consumer end-users, to important
                stakeholders, and internal staff who will be working with the
                product on a daily basis. We leverage user data to better
                understand their needs and create products that make their lives
                better.
              </p>
              <p>
                The ultimate goal of Purpose-Driven Design is to give people
                their time back. We accomplish this by running an efficient
                process and by designing products that reduce friction.
              </p>
              `}
                />
              </Offset>
            </div>
          </Offset>
        </SectionContainer>
        <SectionContainer>
          <CardInfoGroup hasBg {...principlesData}></CardInfoGroup>
        </SectionContainer>
        <SectionContainer>
          <Offset>
            <div class="text-lg bg-white py-24">
              <Offset direction="left" rounded={false}>
                <div className="pl-16">
                  <SectionHeader
                    heading="Our Process"
                    subheading="Our proven process drives projects from kick-off to launch and then into post-launch growth and optimization."
                    as="h1"
                  />
                  <div className="mt-8">
                    <WYSIWYG
                      content={`<p>Our process is what makes us different. It’s one of the unique strengths we bring to every project. We’ve
            spent years fine-tuning it and look forward to using it with you.</p>
          <p>For a deep-dive, check out this article, <a class="italic" href="https://expandtheroom.com/digital-design-process/">Digital Design Process: Drive Successful
              Outcomes</a>.</p>`}
                    />
                  </div>
                </div>
              </Offset>
            </div>
            <Image {...gameboardData} />
          </Offset>
        </SectionContainer>
        <SectionContainer className="container pb-20 md:pb-40">
          <MediaText isImageRounded {...toolkitData} />
        </SectionContainer>
      </div>
    </PageWrap>
  )
}

export default PurposeDrivenDesign

export const query = graphql`
  query($id: String) {
    page: wpPage(id: { eq: $id }) {
      seo {
        ...seoPostTypeFields
      }
    }
  }
`
